import React, { useEffect, useRef, useState } from "react";
import { api, BACKEND_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Smartphone, Apple, Download, ShieldCheck } from "lucide-react";
import { LOGO_MARK, WALLPAPER, TAGLINE } from "../lib/brand";
import { LogoMark } from "../components/Logo";

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
}

export default function AthleteId() {
  const { user } = useAuth();  // eslint-disable-line no-unused-vars
  const [card, setCard] = useState(null);
  const [googleResp, setGoogleResp] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  useEffect(() => {
    api.get("/athlete-id/me").then((r) => setCard(r.data));
  }, []);

  const onMove = (e) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 10;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -10;
    setTilt({ x, y });
  };

  const downloadApple = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/athlete-id/pass/apple`, {
        credentials: "include",
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nordgaming-${card?.member_id || "pass"}.pkpass.json`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const getGoogle = async () => {
    const { data } = await api.get("/athlete-id/pass/google");
    setGoogleResp(data);
  };

  if (!card) return <div className="font-mono text-xs text-white/40">Loading…</div>;

  return (
    <div className="space-y-10 fade-in" data-testid="athlete-id-page">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-primary">Digital Athlete ID</div>
        <h1 className="font-display text-4xl tracking-tight mt-2">Dein Sportlerausweis</h1>
        <p className="text-white/60 text-sm mt-2">
          Premium-Mitgliedskarte für deinen Nordgaming Status. Lade sie in deine Wallet.
        </p>
      </div>

      {/* Card */}
      <div className="flex justify-center">
        <div
          ref={cardRef}
          onMouseMove={onMove}
          onMouseLeave={() => setTilt({ x: 0, y: 0 })}
          style={{ transform: `perspective(1200px) rotateY(${tilt.x}deg) rotateX(${tilt.y}deg)` }}
          className="relative w-full max-w-md aspect-[1.586/1] rounded-sm overflow-hidden tracing-border transition-transform duration-150 ease-out"
          data-testid="athlete-id-card"
        >
          <img
            src={WALLPAPER}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-bg/90 via-bg/70 to-surface/90" />
          <div className="noise-overlay" />

          <div className="relative p-6 h-full flex flex-col justify-between text-white">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2.5">
                <LogoMark src={LOGO_MARK} size="w-8 h-8" />
                <div>
                  <div className="brand-text text-sm leading-none tracking-tight">NORDGAMING</div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/50 mt-1">
                    Athletic ID · Season 26
                  </div>
                </div>
              </div>
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-gradient">{card.club}</div>
            </div>

            <div className="flex items-end gap-4">
              {card.picture ? (
                <img src={card.picture} alt="" className="w-16 h-20 object-cover rounded-sm border border-white/20" />
              ) : (
                <div className="w-16 h-20 bg-neon-gradient-soft border border-primary/40 flex items-center justify-center font-display text-2xl text-gradient rounded-sm">
                  {card.name?.[0]?.toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/50">Member</div>
                <div className="font-display text-xl truncate">{card.name}</div>
                <div className="font-mono text-[10px] tabular-nums text-gradient mt-1 tracking-wider">{card.member_id}</div>
              </div>
              <div className="shrink-0">
                <img
                  src={`${BACKEND_URL}/api/athlete-id/qr?t=${encodeURIComponent(card.member_id)}`}
                  alt="QR"
                  className="w-16 h-16 bg-bg rounded-sm border border-primary/40"
                  data-testid="athlete-id-qr"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 text-white/80">
              <div>
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/50">Role</div>
                <div className="font-mono text-xs mt-1 tabular-nums">{card.role?.toUpperCase()}</div>
              </div>
              <div>
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/50">Game</div>
                <div className="font-mono text-xs mt-1 tabular-nums truncate">{card.favorite_game}</div>
              </div>
              <div>
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/50">Valid until</div>
                <div className="font-mono text-xs mt-1 tabular-nums">{formatDate(card.valid_until)}</div>
              </div>
            </div>

            <div className="absolute bottom-2 right-4 font-mono text-[8px] uppercase tracking-[0.3em] text-white/30">
              {TAGLINE}
            </div>
          </div>
        </div>
      </div>

      {/* Wallet actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto w-full">
        <button
          onClick={downloadApple}
          disabled={downloading}
          className="card-solid p-5 flex items-center gap-4 hover:border-primary/50 transition-colors group text-left"
          data-testid="apple-wallet-download-btn"
        >
          <div className="w-10 h-10 flex items-center justify-center border border-white/20 rounded-sm">
            <Apple className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-display text-base tracking-tight">Apple Wallet</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50 mt-1">
              .pkpass Demo · MOCKED
            </div>
          </div>
          <Download className="w-4 h-4 text-white/40 group-hover:text-primary" />
        </button>

        <button
          onClick={getGoogle}
          className="card-solid p-5 flex items-center gap-4 hover:border-primary/50 transition-colors group text-left"
          data-testid="google-wallet-download-btn"
        >
          <div className="w-10 h-10 flex items-center justify-center border border-white/20 rounded-sm">
            <Smartphone className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-display text-base tracking-tight">Google Wallet</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50 mt-1">
              Save to Google Pay · MOCKED
            </div>
          </div>
          <Download className="w-4 h-4 text-white/40 group-hover:text-primary" />
        </button>
      </div>

      {googleResp && (
        <div className="max-w-2xl mx-auto card p-5 text-sm" data-testid="google-wallet-response">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-primary mb-2">Google Wallet Link (MOCKED)</div>
          <a href={googleResp.add_to_wallet_url} target="_blank" rel="noreferrer" className="text-primary link-underline break-all font-mono text-xs">
            {googleResp.add_to_wallet_url}
          </a>
          <div className="text-white/50 text-xs mt-2">{googleResp.note}</div>
        </div>
      )}

      <div className="max-w-2xl mx-auto card p-4 flex items-start gap-3 text-sm text-white/70">
        <ShieldCheck className="w-4 h-4 text-primary mt-0.5 shrink-0" />
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-primary mb-1">Verifizierung</div>
          Öffentliche Verifizierung über QR-Code oder URL:{" "}
          <a
            className="text-primary link-underline font-mono text-xs"
            href={`/verify/${card.member_id}`}
            target="_blank"
            rel="noreferrer"
            data-testid="verify-link"
          >
            /verify/{card.member_id}
          </a>
        </div>
      </div>
    </div>
  );
}
